import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useToast } from '@/components/hooks/use-toast';
import {
  clearPostRecordCarePacketDownload,
  postRecordCarePacketDownloadRequest,
  selectPostRecordCarePacketDownloadData,
  selectPostRecordCarePacketDownloadErrorMessage,
} from '@client/model';
import { downloadFile } from '@/util/secureDownload';

interface UseCarePacketDownloadProps {
  applicationId: string;
  clientId: string;
  filesToDownload: string[];
  onDownloadComplete: () => void;
}

export function useCarePacketDownload({
  applicationId,
  clientId,
  filesToDownload,
  onDownloadComplete,
}: UseCarePacketDownloadProps) {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const recordData = useSelector(selectPostRecordCarePacketDownloadData);
  const recordError = useSelector(selectPostRecordCarePacketDownloadErrorMessage);
  const [isDownloadingFiles, setIsDownloadingFiles] = useState(false);
  const [isAwaitingDownloadRecord, setIsAwaitingDownloadRecord] = useState(false);

  useEffect(() => {
    if (!isAwaitingDownloadRecord || !recordData) return;

    toast({
      title: 'Download Complete',
      description: 'Care packet documents have been downloaded successfully.',
      className: 'bg-green-50 text-green-800',
    });
    onDownloadComplete();
    setIsAwaitingDownloadRecord(false);
    setIsDownloadingFiles(false);

    dispatch(clearPostRecordCarePacketDownload());
  }, [dispatch, isAwaitingDownloadRecord, onDownloadComplete, recordData, toast]);

  useEffect(() => {
    if (!isAwaitingDownloadRecord || !recordError) return;

    toast({
      title: 'Download Recording Failed',
      description: recordError,
      className: 'bg-red-50 text-red-800',
    });
    setIsAwaitingDownloadRecord(false);
    setIsDownloadingFiles(false);

    dispatch(clearPostRecordCarePacketDownload());
  }, [dispatch, isAwaitingDownloadRecord, recordError, toast]);

  const downloadCarePacket = useCallback(async () => {
    setIsDownloadingFiles(true);
    dispatch(clearPostRecordCarePacketDownload());

    try {
      for (let index = 0; index < filesToDownload.length; index += 1) {
        const result = await downloadFile(
          filesToDownload[index],
          `${applicationId}-document-${index + 1}.pdf`,
        );

        if (!result.success) {
          throw new Error(result.error || 'Failed to download care packet');
        }
      }

      setIsAwaitingDownloadRecord(true);
      dispatch(
        postRecordCarePacketDownloadRequest({
          clientPolicyApplicationId: applicationId,
          clientId,
          downloadedFiles: filesToDownload,
          metadata: {
            downloadCompletedAt: new Date().toISOString(),
            fileCount: filesToDownload.length,
          },
        }),
      );
    } catch (error) {
      toast({
        title: 'Download Failed',
        description:
          error instanceof Error ? error.message : 'Failed to download care packet documents.',
        className: 'bg-red-50 text-red-800',
      });
      setIsDownloadingFiles(false);
    }
  }, [applicationId, clientId, dispatch, filesToDownload, toast]);

  return {
    downloadCarePacket,
    isDownloading: isDownloadingFiles,
  };
}
